import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { eventService } from '../../services/firebase/eventService';
import { emailService } from '../../services/email/emailService';
import './Events.css';

const EventsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const [upcomingEvents, past] = await Promise.all([
        eventService.getUpcoming(50),
        eventService.getPast(20)
      ]);
      setEvents(upcomingEvents);
      setPastEvents(past);
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Unable to load events. Please try again later.');
      setEvents([]);
      setPastEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString('en-AU', options);
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getStatusBadge = (event) => {
    if (event.status === 'full' || event.spots?.available <= 0) return 'Full';
    if (event.status === 'filling-fast' || event.spots?.available <= 3) return 'Filling Fast';
    return `${event.spots?.available || 0} Spots Available`;
  };

  const getStatusClass = (event) => {
    if (event.status === 'full' || event.spots?.available <= 0) return 'event-badge--full';
    if (event.status === 'filling-fast' || event.spots?.available <= 3) return 'event-badge--filling';
    return 'event-badge--open';
  };

  const displayEvents = activeTab === 'upcoming' ? events : pastEvents;
  const categories = ['all', ...new Set(displayEvents.map(e => e.category).filter(Boolean))];

  const filteredEvents = filter === 'all'
    ? displayEvents
    : displayEvents.filter(event => event.category === filter);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowRegistration(true);
    setRegistrationSuccess(false);
    setFormData({ name: '', email: '', phone: '', notes: '' });
  };

  const handleCloseModal = () => {
    setShowRegistration(false);
    setSelectedEvent(null);
    setRegistrationSuccess(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await eventService.registerAttendee(selectedEvent.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      });

      // Send confirmation emails
      try {
        await emailService.init();

        const attendee = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          notes: formData.notes
        };

        // Send confirmation to the customer
        const customerResult = await emailService.sendEventRegistrationConfirmation(selectedEvent, attendee);
        if (!customerResult.success) {
          console.warn('Event registration customer email failed:', customerResult.error);
        }

        // Send notification to admin (Kirstie)
        const adminResult = await emailService.sendEventRegistrationNotificationToAdmin(selectedEvent, attendee);
        if (!adminResult.success) {
          console.warn('Event registration admin email failed:', adminResult.error);
        }
      } catch (emailErr) {
        console.error('Error sending event registration emails:', emailErr);
      }

      setRegistrationSuccess(true);
      // Refresh events to update spots
      loadEvents();
    } catch (err) {
      console.error('Error registering:', err);
      if (err.message === 'Event is full') {
        setError('Sorry, this event is now full. Please choose another event.');
        loadEvents();
      } else if (err.message === 'You are already registered for this event') {
        setError('You are already registered for this event.');
      } else {
        setError('There was an error submitting your registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookNow = (event, e) => {
    e.stopPropagation();
    if (event.status === 'full' || event.spots?.available <= 0) return;
    handleEventClick(event);
  };

  const getTeamDisplay = (event) => {
    const team = event.assignedTeam || event.team;
    if (!team || team.length === 0) return null;
    const hosts = team.filter(t => t.role === 'host');
    if (hosts.length === 0) return null;
    return hosts.map(h => h.memberName || h.name).join(' & ');
  };

  const isPastEvent = (event) => {
    const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
    return eventDate < new Date();
  };

  return (
    <>
      <SEO
        title="Events & Workshops | Meditation & Reiki Events Sydney"
        description="Join transformative wellness workshops, Reiki training, meditation circles, sound baths and healing events in Sydney. Limited spots available — book today with KO Holistic Health."
        url="/events"
        keywords="meditation workshops sydney, reiki training events, healing events sydney, spiritual workshops, wellness events sydney, sound bath sydney, meditation circle, breathwork workshop, holistic health events, energy healing workshop"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Events & Workshops', path: '/events' }]}
      />

      <div className="events-page">
        <section className="events-page-hero">
          <div className="container">
            <h1>Events & Workshops</h1>
            <p className="events-page-hero__subtitle">
              Join our community for transformative experiences, learning, and connection
            </p>
          </div>
        </section>

        <section className="section events-page-content">
          <div className="container">
            {/* Tab Toggle */}
            <div className="events-tabs">
              <button
                className={`events-tab ${activeTab === 'upcoming' ? 'events-tab--active' : ''}`}
                onClick={() => { setActiveTab('upcoming'); setFilter('all'); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Upcoming Events
                {events.length > 0 && <span className="events-tab__count">{events.length}</span>}
              </button>
              <button
                className={`events-tab ${activeTab === 'past' ? 'events-tab--active' : ''}`}
                onClick={() => { setActiveTab('past'); setFilter('all'); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Past Events
                {pastEvents.length > 0 && <span className="events-tab__count">{pastEvents.length}</span>}
              </button>
            </div>

            {loading ? (
              <div className="events-loading">
                <div className="spinner"></div>
                <p>Loading events...</p>
              </div>
            ) : error && displayEvents.length === 0 ? (
              <div className="events-error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p>{error}</p>
                <Button onClick={loadEvents}>Try Again</Button>
              </div>
            ) : displayEvents.length === 0 ? (
              <div className="no-events">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <h2>{activeTab === 'upcoming' ? 'No Upcoming Events' : 'No Past Events'}</h2>
                <p>
                  {activeTab === 'upcoming'
                    ? 'Check back soon for new workshops, classes, and special events.'
                    : 'Past events will appear here once they have concluded.'}
                </p>
                {activeTab === 'upcoming' && (
                  <>
                    <p>In the meantime, why not book a private session?</p>
                    <Button onClick={() => navigate('/booking')}>Book a Session</Button>
                  </>
                )}
              </div>
            ) : (
              <>
                {categories.length > 2 && (
                  <div className="events-filter">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        className={filter === cat ? 'filter-btn active' : 'filter-btn'}
                        onClick={() => setFilter(cat)}
                      >
                        {cat === 'all' ? 'All Events' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="events-page-grid">
                  {filteredEvents.map((event) => (
                    <Card
                      key={event.id}
                      className={`event-page-card ${activeTab === 'past' ? 'event-page-card--past' : ''}`}
                      onClick={() => handleEventClick(event)}
                    >
                      {/* Event Image */}
                      {event.image && (
                        <div className="event-page-card__image">
                          <img src={event.image} alt={event.title} />
                          {activeTab === 'past' && (
                            <div className="event-page-card__past-overlay">
                              <span>Past Event</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="event-page-card__body">
                        <div className="event-page-card__header">
                          {activeTab === 'upcoming' ? (
                            <span className={`event-badge ${getStatusClass(event)}`}>
                              {getStatusBadge(event)}
                            </span>
                          ) : (
                            <span className="event-badge event-badge--past">Completed</span>
                          )}
                          {event.category && (
                            <span className="event-category">{event.category}</span>
                          )}
                        </div>

                        <div className="event-page-card__date">
                          <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"/>
                          </svg>
                          <div>
                            <p className="event-date">{formatShortDate(event.date)}</p>
                            <p className="event-time">
                              {event.time}
                              {event.endTime && ` - ${event.endTime}`}
                            </p>
                          </div>
                        </div>

                        <h3 className="event-page-card__title">{event.title}</h3>

                        {getTeamDisplay(event) && (
                          <p className="event-page-card__host">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                              <circle cx="12" cy="7" r="4"/>
                            </svg>
                            Hosted by {getTeamDisplay(event)}
                          </p>
                        )}

                        <p className="event-page-card__location">
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                          </svg>
                          {event.location}
                        </p>

                        {event.description && (
                          <p className="event-page-card__description">
                            {event.description.length > 150
                              ? `${event.description.substring(0, 150)}...`
                              : event.description}
                          </p>
                        )}

                        {event.benefits && event.benefits.length > 0 && (
                          <div className="event-benefits">
                            <h4>What you'll gain:</h4>
                            <ul>
                              {event.benefits.slice(0, 3).map((benefit, index) => (
                                <li key={index}>{benefit}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="event-page-card__footer">
                          <div className="event-footer-left">
                            <span className="event-price">
                              {event.price === 0 ? 'Free' : `$${event.price} AUD`}
                            </span>
                            {activeTab === 'upcoming' && (
                              <span className="event-spots">
                                {event.spots?.available}/{event.spots?.total} spots
                              </span>
                            )}
                            {activeTab === 'past' && event.attendees && (
                              <span className="event-spots">
                                {event.attendees.length} attended
                              </span>
                            )}
                          </div>
                          {activeTab === 'upcoming' ? (
                            <Button
                              variant={event.status === 'full' || event.spots?.available <= 0 ? 'secondary' : 'primary'}
                              disabled={event.status === 'full' || event.spots?.available <= 0}
                              onClick={(e) => handleBookNow(event, e)}
                            >
                              {event.status === 'full' || event.spots?.available <= 0 ? 'Fully Booked' : 'Book Now'}
                            </Button>
                          ) : (
                            <Button variant="secondary" onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}>
                              View Details
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {filteredEvents.length === 0 && (
                  <div className="no-events">
                    <p>No events found in this category.</p>
                    <Button onClick={() => setFilter('all')} variant="secondary">
                      View All Events
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Registration Modal */}
      {showRegistration && selectedEvent && (
        <div className="event-modal-overlay" onClick={handleCloseModal}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button className="event-modal__close" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            {registrationSuccess ? (
              <div className="event-modal__success">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <h2>Registration Successful!</h2>
                <p>You're registered for <strong>{selectedEvent.title}</strong></p>
                <div className="event-modal__summary">
                  <p><strong>Date:</strong> {formatDate(selectedEvent.date)}</p>
                  <p><strong>Time:</strong> {selectedEvent.time}{selectedEvent.endTime && ` - ${selectedEvent.endTime}`}</p>
                  <p><strong>Location:</strong> {selectedEvent.location}</p>
                </div>
                <p className="event-modal__notice">
                  A confirmation email has been sent. Please check your inbox for details.
                </p>
                <Button onClick={handleCloseModal}>Close</Button>
              </div>
            ) : (
              <>
                {/* Modal Image */}
                {selectedEvent.image && (
                  <div className="event-modal__image">
                    <img src={selectedEvent.image} alt={selectedEvent.title} />
                  </div>
                )}

                <div className="event-modal__header">
                  {!isPastEvent(selectedEvent) ? (
                    <span className={`event-badge ${getStatusClass(selectedEvent)}`}>
                      {getStatusBadge(selectedEvent)}
                    </span>
                  ) : (
                    <span className="event-badge event-badge--past">Past Event</span>
                  )}
                  <h2>{selectedEvent.title}</h2>
                </div>

                <div className="event-modal__details">
                  <div className="event-modal__info">
                    <div className="event-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span>{formatDate(selectedEvent.date)}</span>
                    </div>
                    <div className="event-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>{selectedEvent.time}{selectedEvent.endTime && ` - ${selectedEvent.endTime}`}</span>
                    </div>
                    <div className="event-modal__info-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                    {getTeamDisplay(selectedEvent) && (
                      <div className="event-modal__info-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                        <span>Hosted by {getTeamDisplay(selectedEvent)}</span>
                      </div>
                    )}
                  </div>

                  <div className="event-modal__price-box">
                    <span className="event-modal__price">
                      {selectedEvent.price === 0 ? 'Free' : `$${selectedEvent.price}`}
                    </span>
                    {!isPastEvent(selectedEvent) && (
                      <span className="event-modal__spots">
                        {selectedEvent.spots?.available} spots remaining
                      </span>
                    )}
                  </div>
                </div>

                {selectedEvent.description && (
                  <div className="event-modal__description">
                    <p>{selectedEvent.description}</p>
                  </div>
                )}

                {/* Benefits Section */}
                {selectedEvent.benefits && selectedEvent.benefits.length > 0 && (
                  <div className="event-modal__section">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 11 12 14 22 4"/>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                      What You'll Gain
                    </h4>
                    <ul className="event-modal__benefits">
                      {selectedEvent.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* What to Bring */}
                {selectedEvent.whatToBring && (
                  <div className="event-modal__section event-modal__section--highlight">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      What to Bring
                    </h4>
                    <p>{selectedEvent.whatToBring}</p>
                  </div>
                )}

                {/* Requirements */}
                {selectedEvent.requirements && (
                  <div className="event-modal__section event-modal__section--highlight">
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      Requirements
                    </h4>
                    <p>{selectedEvent.requirements}</p>
                  </div>
                )}

                {/* Registration Form - Only for upcoming events */}
                {!isPastEvent(selectedEvent) && selectedEvent.spots?.available > 0 && (
                  <form onSubmit={handleSubmit} className="event-modal__form">
                    <h3>Register for this Event</h3>

                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="0400 000 000"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="notes">Additional Notes (Optional)</label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Any dietary requirements, accessibility needs, or questions..."
                      ></textarea>
                    </div>

                    {error && (
                      <div className="form-error">
                        {error}
                      </div>
                    )}

                    {selectedEvent.cancellationPolicy && (
                      <p className="event-modal__policy">
                        <strong>Cancellation Policy:</strong> {selectedEvent.cancellationPolicy}
                      </p>
                    )}

                    <Button
                      type="submit"
                      size="large"
                      fullWidth
                      disabled={isSubmitting || selectedEvent.spots?.available <= 0}
                    >
                      {isSubmitting ? 'Registering...' : 'Confirm Registration'}
                    </Button>
                  </form>
                )}

                {/* Past event notice */}
                {isPastEvent(selectedEvent) && (
                  <div className="event-modal__past-notice">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <p>This event has already taken place. Check our upcoming events for similar experiences.</p>
                    <Button variant="secondary" onClick={() => { handleCloseModal(); setActiveTab('upcoming'); }}>
                      View Upcoming Events
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EventsPage;
